/**
 * StorePage - Digital Goods Store
 * Browse and purchase sample packs, plugins, and bundles
 */

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import ShoppingCartPanel from '../components/store/ShoppingCart';
import ProductReviews from '../components/store/ProductReviews';
import {
  Store,
  Search,
  Filter,
  Music,
  Plug,
  Package,
  Star,
  Download,
  ShoppingCart,
  ShoppingBag,
  ChevronDown,
  X,
  Loader2,
  Play,
  Pause,
  Headphones,
  Layers,
  Sparkles,
  TrendingUp,
  Clock,
  Check,
  Heart,
  Share2,
  ArrowRight,
  Tag,
  Zap,
} from 'lucide-react';
import { getBackendUrl } from '../config/api';

// Category icons and colors
const CATEGORY_CONFIG = {
  sample_pack: {
    icon: Music,
    label: 'Sample Packs',
    color: 'emerald',
    gradient: 'from-emerald-500 to-teal-500',
  },
  plugin: {
    icon: Plug,
    label: 'Plugins',
    color: 'violet',
    gradient: 'from-violet-500 to-indigo-500',
  },
  preset_bundle: {
    icon: Layers,
    label: 'Bundles',
    color: 'amber',
    gradient: 'from-amber-500 to-orange-500',
  },
  course: {
    icon: Headphones,
    label: 'Courses',
    color: 'pink',
    gradient: 'from-pink-500 to-rose-500',
  },
};

// Sort options
const SORT_OPTIONS = [
  { value: 'popular', label: 'Most Popular' },
  { value: 'newest', label: 'Newest First' },
  { value: 'price_low', label: 'Price: Low to High' },
  { value: 'price_high', label: 'Price: High to Low' },
  { value: 'rating', label: 'Highest Rated' },
];

// Star rating display
const StarRating = ({ rating, size = 'sm' }) => {
  const sizeClasses = { xs: 'w-3 h-3', sm: 'w-4 h-4', md: 'w-5 h-5' };
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${sizeClasses[size]} ${
            star <= rating ? 'fill-amber-400 text-amber-400' : 'fill-zinc-600 text-zinc-600'
          }`}
        />
      ))}
    </div>
  );
};

// Product card component
const ProductCard = ({ product, onAddToCart, onQuickView }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);
  
  const config = CATEGORY_CONFIG[product.category] || CATEGORY_CONFIG.sample_pack;
  const Icon = config.icon;
  const discount = product.original_price 
    ? Math.round((1 - product.price / product.original_price) * 100)
    : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="bg-zinc-900/50 rounded-2xl border border-zinc-800 overflow-hidden group cursor-pointer"
      onClick={() => onQuickView(product)}
      data-testid={`product-card-${product.id}`}
    >
      {/* Image/Preview Area */}
      <div className={`relative h-48 bg-gradient-to-br ${config.gradient} overflow-hidden`}>
        {/* Category icon background */}
        <div className="absolute inset-0 flex items-center justify-center opacity-20">
          <Icon className="w-32 h-32 text-white" />
        </div>
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {discount > 0 && (
            <span className="px-2 py-1 bg-red-500 text-white text-xs font-bold rounded-lg shadow-lg">
              -{discount}%
            </span>
          )}
          {product.featured && (
            <span className="px-2 py-1 bg-amber-500 text-black text-xs font-bold rounded-lg shadow-lg flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              Featured
            </span>
          )}
        </div>

        {/* Wishlist button */}
        <button
          onClick={(e) => { e.stopPropagation(); setIsWishlisted(!isWishlisted); }}
          className={`absolute top-3 right-3 p-2 rounded-full transition-all ${
            isWishlisted 
              ? 'bg-red-500 text-white' 
              : 'bg-black/30 text-white/70 hover:bg-black/50 hover:text-white'
          }`}
        >
          <Heart className={`w-4 h-4 ${isWishlisted ? 'fill-current' : ''}`} />
        </button>

        {/* Quick actions overlay */}
        <AnimatePresence>
          {isHovered && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center gap-3"
            >
              <Button
                onClick={(e) => { e.stopPropagation(); onQuickView(product); }}
                variant="secondary"
                size="sm"
                className="bg-white/20 hover:bg-white/30 text-white"
              >
                Quick View
              </Button>
              <Button
                onClick={(e) => { e.stopPropagation(); onAddToCart(product); }}
                size="sm"
                className={`bg-gradient-to-r ${config.gradient} hover:opacity-90`}
              >
                <ShoppingCart className="w-4 h-4 mr-1" />
                Add to Cart
              </Button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Category badge */}
        <div className="absolute bottom-3 left-3">
          <span className="px-3 py-1 bg-black/30 backdrop-blur-sm text-white text-xs rounded-full flex items-center gap-1">
            <Icon className="w-3 h-3" />
            {config.label}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-semibold text-white mb-1 line-clamp-1 group-hover:text-white/90">
          {product.name}
        </h3>
        <p className="text-sm text-zinc-500 mb-3 line-clamp-2">
          {product.description}
        </p>

        {/* Meta info */}
        <div className="flex items-center gap-3 text-xs text-zinc-500 mb-3">
          <div className="flex items-center gap-1">
            <StarRating rating={product.rating || 0} size="xs" />
            <span>({product.rating?.toFixed(1) || '0.0'})</span>
          </div>
          <span className="flex items-center gap-1">
            <Download className="w-3 h-3" />
            {product.downloads || 0}
          </span>
          {product.sample_count && (
            <span className="flex items-center gap-1">
              <Music className="w-3 h-3" />
              {product.sample_count} samples
            </span>
          )}
        </div>

        {/* Tags */}
        {product.tags && product.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {product.tags.slice(0, 3).map((tag, i) => (
              <span key={i} className="px-2 py-0.5 bg-zinc-800 text-zinc-400 text-xs rounded">
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Price */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`text-xl font-bold text-${config.color}-400`}>
              ${product.price}
            </span>
            {product.original_price && product.original_price > product.price && (
              <span className="text-sm text-zinc-500 line-through">
                ${product.original_price}
              </span>
            )}
          </div>
          <Button
            onClick={(e) => { e.stopPropagation(); onAddToCart(product); }}
            size="sm"
            className={`bg-gradient-to-r ${config.gradient} hover:opacity-90`}
          >
            <ShoppingCart className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

// Product detail modal
const ProductDetailModal = ({ product, isOpen, onClose, onAddToCart }) => {
  if (!product) return null;
  
  const config = CATEGORY_CONFIG[product.category] || CATEGORY_CONFIG.sample_pack;
  const Icon = config.icon;
  const discount = product.original_price 
    ? Math.round((1 - product.price / product.original_price) * 100)
    : 0;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-zinc-900 border border-zinc-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
          >
            {/* Header image */}
            <div className={`relative h-48 bg-gradient-to-br ${config.gradient}`}>
              <div className="absolute inset-0 flex items-center justify-center opacity-20">
                <Icon className="w-40 h-40 text-white" />
              </div>
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 bg-black/30 rounded-full text-white hover:bg-black/50"
              >
                <X className="w-5 h-5" />
              </button>
              {discount > 0 && (
                <span className="absolute top-4 left-4 px-3 py-1 bg-red-500 text-white text-sm font-bold rounded-lg">
                  Save {discount}%
                </span>
              )}
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-12rem)]">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <span className={`text-sm text-${config.color}-400 font-medium`}>
                    {config.label}
                  </span>
                  <h2 className="text-2xl font-bold text-white mt-1">{product.name}</h2>
                  <p className="text-zinc-400 text-sm mt-1">by {product.creator_name || 'String Sauce'}</p>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-2">
                    <span className={`text-3xl font-bold text-${config.color}-400`}>
                      ${product.price}
                    </span>
                    {product.original_price && (
                      <span className="text-lg text-zinc-500 line-through">
                        ${product.original_price}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 mt-1 justify-end">
                    <StarRating rating={product.rating || 0} size="sm" />
                    <span className="text-zinc-500 text-sm">({product.downloads || 0} sales)</span>
                  </div>
                </div>
              </div>

              <p className="text-zinc-300 mb-6">{product.description}</p>

              {/* Features */}
              {product.features && product.features.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-zinc-400 uppercase mb-3">Features</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {product.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2 text-zinc-300">
                        <Check className={`w-4 h-4 text-${config.color}-400`} />
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Meta info */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-zinc-800/50 rounded-xl mb-6">
                {product.sample_count && (
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{product.sample_count}</p>
                    <p className="text-xs text-zinc-500">Samples</p>
                  </div>
                )}
                {product.file_size && (
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{product.file_size}</p>
                    <p className="text-xs text-zinc-500">File Size</p>
                  </div>
                )}
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">{product.downloads || 0}</p>
                  <p className="text-xs text-zinc-500">Downloads</p>
                </div>
              </div>

              {/* Tags */}
              {product.tags && product.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {product.tags.map((tag, i) => (
                    <span key={i} className="px-3 py-1 bg-zinc-800 text-zinc-400 text-sm rounded-full">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Compatibility */}
              {product.compatible_with && product.compatible_with.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-zinc-400 uppercase mb-2">Compatible With</h3>
                  <p className="text-zinc-300 text-sm">{product.compatible_with.join(', ')}</p>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-3 mb-6">
                <Button
                  onClick={() => onAddToCart(product)}
                  className={`flex-1 bg-gradient-to-r ${config.gradient} hover:opacity-90 py-6`}
                >
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  Add to Cart - ${product.price}
                </Button>
                <Button
                  variant="outline"
                  className="px-4 border-zinc-700"
                >
                  <Heart className="w-5 h-5" />
                </Button>
                <Button
                  variant="outline"
                  className="px-4 border-zinc-700"
                >
                  <Share2 className="w-5 h-5" />
                </Button>
              </div>

              {/* Reviews section */}
              <div className="border-t border-zinc-800 pt-6">
                <ProductReviews productId={product.id} productName={product.name} />
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Main StorePage component
const StorePage = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [sortBy, setSortBy] = useState('popular');
  const [categories, setCategories] = useState({});
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [cart, setCart] = useState({ items: [], total: 0, item_count: 0 });
  const [showFilters, setShowFilters] = useState(false);
  const [showCart, setShowCart] = useState(false);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        ...(selectedCategory && { category: selectedCategory }),
        ...(searchQuery && { search: searchQuery }),
        sort_by: sortBy,
        limit: '50',
      });

      const response = await fetch(`${getBackendUrl()}/api/store/products?${params}`);
      if (!response.ok) throw new Error('Failed to fetch products');

      const data = await response.json();
      setProducts(data.products || []);
      setCategories(data.categories || {});
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, searchQuery, sortBy]);

  // Fetch user's cart from backend
  const fetchCart = useCallback(async () => {
    if (!user) {
      setCart({ items: [], total: 0, item_count: 0 });
      return;
    }

    try {
      const response = await fetch(`${getBackendUrl()}/api/store/cart/${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setCart(data);
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  }, [user]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const handleAddToCart = async (product) => {
    if (!user) {
      toast.error('Please login to add items to cart');
      return;
    }

    try {
      const response = await fetch(`${getBackendUrl()}/api/store/cart/${user.id}/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: product.id,
          quantity: 1,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to add to cart');
      }

      toast.success(`Added ${product.name} to cart`);
      fetchCart(); // Refresh cart
    } catch (error) {
      toast.error(error.message || 'Failed to add to cart');
    }
  };

  const totalCategories = Object.values(categories).reduce((a, b) => a + b, 0);

  return (
    <div className="min-h-screen bg-zinc-950 text-white" data-testid="store-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-zinc-950/80 backdrop-blur-xl border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            {/* Logo and title */}
            <div className="flex items-center gap-3">
              <Link to="/" className="flex items-center gap-2">
                <img src="/images/string-sauce-logo.png" alt="String Sauce" className="h-8 w-auto" />
              </Link>
              <div className="hidden md:block">
                <h1 className="text-lg font-bold">Digital Store</h1>
                <p className="text-xs text-zinc-500">{totalCategories} products available</p>
              </div>
            </div>

            {/* Search */}
            <div className="flex-1 max-w-xl">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <input
                  type="text"
                  placeholder="Search samples, plugins, bundles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-zinc-900 border border-zinc-800 rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="store-search-input"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                className="border-zinc-700 md:hidden"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="w-5 h-5" />
              </Button>
              <Link to="/leaderboard">
                <Button variant="outline" className="border-zinc-700 hidden md:flex">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Leaderboard
                </Button>
              </Link>
              {user && (
                <Link to="/purchases">
                  <Button variant="outline" className="border-zinc-700">
                    <ShoppingBag className="w-5 h-5 mr-2" />
                    <span className="hidden sm:inline">My Purchases</span>
                  </Button>
                </Link>
              )}
              <Button
                variant="outline"
                onClick={() => setShowCart(true)}
                className="relative border-zinc-700"
                data-testid="cart-button"
              >
                <ShoppingCart className="w-5 h-5" />
                {cart.item_count > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-violet-500 rounded-full text-xs flex items-center justify-center">
                    {cart.item_count}
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex gap-8">
          {/* Sidebar filters */}
          <aside className={`w-64 shrink-0 ${showFilters ? 'block' : 'hidden'} md:block`}>
            {/* Categories */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-zinc-400 uppercase mb-3">Categories</h3>
              <div className="space-y-1">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors ${
                    !selectedCategory
                      ? 'bg-violet-500/20 text-violet-400'
                      : 'text-zinc-400 hover:bg-zinc-800'
                  }`}
                  data-testid="category-all"
                >
                  <span className="flex items-center gap-2">
                    <Store className="w-4 h-4" />
                    All Products
                  </span>
                  <span className="text-xs">{totalCategories}</span>
                </button>
                {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
                  const Icon = config.icon;
                  const count = categories[key] || 0;
                  return (
                    <button
                      key={key}
                      onClick={() => setSelectedCategory(key)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors ${
                        selectedCategory === key
                          ? `bg-${config.color}-500/20 text-${config.color}-400`
                          : 'text-zinc-400 hover:bg-zinc-800'
                      }`}
                      data-testid={`category-${key}`}
                    >
                      <span className="flex items-center gap-2">
                        <Icon className="w-4 h-4" />
                        {config.label}
                      </span>
                      <span className="text-xs">{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Sort */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-zinc-400 uppercase mb-3">Sort By</h3>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-300 focus:outline-none focus:ring-2 focus:ring-violet-500"
                data-testid="sort-select"
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Featured banner */}
            <div className="p-4 bg-gradient-to-br from-violet-500/20 to-indigo-500/20 rounded-xl border border-violet-500/30">
              <Sparkles className="w-6 h-6 text-violet-400 mb-2" />
              <h4 className="font-semibold text-white mb-1">Producer Bundle</h4>
              <p className="text-xs text-zinc-400 mb-3">Save 60% on our starter bundle</p>
              <Button size="sm" className="w-full bg-violet-500 hover:bg-violet-600">
                View Bundle
              </Button>
            </div>
          </aside>

          {/* Main content */}
          <main className="flex-1">
            {/* Results header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold">
                  {selectedCategory ? CATEGORY_CONFIG[selectedCategory]?.label : 'All Products'}
                </h2>
                <p className="text-sm text-zinc-500">
                  {products.length} product{products.length !== 1 ? 's' : ''} found
                </p>
              </div>
              <div className="hidden md:flex items-center gap-2">
                <span className="text-sm text-zinc-500">Sort:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-1.5 bg-zinc-900 border border-zinc-800 rounded-lg text-sm text-zinc-300 focus:outline-none"
                >
                  {SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Products grid */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-violet-400" />
              </div>
            ) : products.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onAddToCart={handleAddToCart}
                    onQuickView={setSelectedProduct}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <Package className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-zinc-400 mb-2">No products found</h3>
                <p className="text-zinc-500 mb-4">Try adjusting your search or filters</p>
                <Button onClick={() => { setSearchQuery(''); setSelectedCategory(null); }}>
                  Clear Filters
                </Button>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Product detail modal */}
      <ProductDetailModal
        product={selectedProduct}
        isOpen={!!selectedProduct}
        onClose={() => setSelectedProduct(null)}
        onAddToCart={handleAddToCart}
      />

      {/* Shopping Cart Panel */}
      <ShoppingCartPanel
        isOpen={showCart}
        onClose={() => setShowCart(false)}
        cart={cart}
        onUpdateCart={fetchCart}
        user={user}
      />
    </div>
  );
};

export default StorePage;
